'use client'

import { useTranslations, useResponsive } from '@dustin-web/microsite-components/src/hooks'
import React, { useCallback, useEffect } from 'react'
import { ProductCardGallery } from '../cards'
import LegacyPopoverInitializer from '../legacy-popover-initializer'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import { GET_RECENTLY_VIEWED } from '@dustin-web/microsite-data/src/graphql/product/get-recently-viewed.graphql'
import { GetRecentlyViewedQuery } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import RecentlyViewedProduct from './recently-viewed-product'
import LazyHydrate from 'react-lazy-hydration'
import { useGraphql } from '@dustin-web/microsite-data/src/hooks'
import { Helpers } from '../utils/product-digital-data'
import OnScreen from '@dustin-web/microsite-components/src/on-screen'

type Props = {
  className?: string
  hideRecentlyViewedDesktop?: boolean
}

const RecentlyViewed = ({ className, hideRecentlyViewedDesktop = false }: Props) => {
  const { translate } = useTranslations()
  const { windowSize: viewportSize, breakpoints } = useResponsive()

  const recentlyViewedAsSidebar = hideRecentlyViewedDesktop && viewportSize.width > 1400

  const getSizeClasses = () => {
    if (recentlyViewedAsSidebar) {
      return {
        sizeClass1: 'col-12 col-sm-6 col-md-3 d-lg-block',
        sizeClass2: 'col-12 col-sm-6 col-lg-3',
      }
    } else {
      return {
        sizeClass1: 'col-12 col-sm-6 col-md-4',
        sizeClass2: 'col-12 col-sm-6 col-lg-4',
      }
    }
  }

  const getTakeCount = () => {
    if (recentlyViewedAsSidebar) {
      return breakpoints.max.MD ? 3 : 4
    }

    if (breakpoints.max.LG) {
      return breakpoints.max.MD ? 4 : 2
    }

    return 3
  }

  const origData = useGraphql<GetRecentlyViewedQuery>(GET_RECENTLY_VIEWED, {})

  useEffect(() => {
    LegacyPopoverInitializer.init()
  })

  const data = origData.data

  const getProductReferences = useCallback(
    () =>
      data?.recentlyViewed?.products.productReferences.filter(
        productReference => productReference.searchResultProduct
      ) ?? [],
    [data]
  )

  const productReferences = getProductReferences()
  const relatedProductsTakeCount = getTakeCount()

  const getRelatedProductReferences = useCallback(
    () =>
      data?.recentlyViewed?.relatedProducts.productReferences.filter(
        (productReference, i) =>
          productReference.searchResultProduct && i < relatedProductsTakeCount
      ) ?? [],
    [relatedProductsTakeCount, data]
  )

  const relatedProductReferences = getRelatedProductReferences()

  if (productReferences.length === 0 || relatedProductReferences.length === 0) {
    return null
  }
  const sizeClasses = getSizeClasses()
  return (
    <div className={`c-box c-box--section u-bg-gray-02 ${className}`}>
      <div className="container">
        <div className="row sm-gutters">
          {!recentlyViewedAsSidebar && (
            <section
              className="col-md-4 col-lg-3 u-mb-24 u-mb-md-0"
              data-gasource="RecentlyViewedProducts">
              <h2 className="h3">{translate('RecentlyViewed')}</h2>

              <OnScreen
                handleOnScreen={() =>
                  Helpers.formatAndPushViewItemList(productReferences, 'recentlyViewed')
                }
                className="c-box u-px-0 u-py-0">
                {productReferences.map(
                  (productReference, i) =>
                    productReference.searchResultProduct && (
                      <RecentlyViewedProduct
                        digitalDataOrigin={'recentlyViewed'}
                        digitalDataIndex={i}
                        key={i}
                        product={productReference.searchResultProduct}
                        ticket={productReference.ticket}
                        isLastItem={i === productReferences.length - 1}
                      />
                    )
                )}
              </OnScreen>
            </section>
          )}

          <section
            className={`${!recentlyViewedAsSidebar ? 'col-12 col-md-8 col-lg-9' : 'col-12'}`}
            data-gasource="RecommendedProducts">
            <h4 className="h3">{translate('RelatedToRecentlyViewed')}</h4>
            <div className="row sm-gutters">
              {relatedProductReferences.map(
                (productReference, i) =>
                  productReference.searchResultProduct && (
                    <ProductCardGallery
                      product={productReference.searchResultProduct}
                      ticket={productReference.ticket}
                      key={i}
                      sizeClasses={
                        breakpoints.max.LG
                          ? i > 1
                            ? sizeClasses.sizeClass1
                            : sizeClasses.sizeClass2
                          : i === 2
                          ? sizeClasses.sizeClass1
                          : sizeClasses.sizeClass2
                      }
                      digitalDataOrigin={'recentlyViewed'}
                      digitalIndex={i}
                    />
                  )
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

const RecentlyViewedWithErrorBoundary = ({ hideRecentlyViewedDesktop }: Props) => (
  <ErrorBoundary>
    <LazyHydrate whenVisible>
      <RecentlyViewed hideRecentlyViewedDesktop={hideRecentlyViewedDesktop} />
    </LazyHydrate>
  </ErrorBoundary>
)

export default RecentlyViewedWithErrorBoundary
